// download.ts — client-side file download helpers, extracted from the
// blob+anchor idiom previously duplicated in Reports.tsx and RosterTab.tsx.
//
// The app has no XLSX library (and we don't add one), so "export to
// spreadsheet" means a CSV that Excel/Numbers/Sheets open natively. The
// anchor is appended to the DOM before .click() and removed after — Firefox
// won't fire the download otherwise.

/** Trigger a browser download of arbitrary text content. */
export function downloadText(filename: string, mime: string, text: string): void {
  const blob = new Blob([text], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/** RFC-4180-ish escaping: quote if the cell contains a comma, quote or newline. */
export function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value)
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

/**
 * Build a CSV string from a matrix of rows (first row is usually the header).
 * A leading UTF-8 BOM is prepended so Excel renders £ and accented names
 * correctly.
 */
export function toCsv(rows: ReadonlyArray<ReadonlyArray<unknown>>): string {
  const body = rows.map((row) => row.map(csvCell).join(',')).join('\r\n')
  return '﻿' + body
}

/** Convenience: build a CSV from rows and download it as text/csv. */
export function downloadCsv(filename: string, rows: ReadonlyArray<ReadonlyArray<unknown>>): void {
  downloadText(filename, 'text/csv;charset=utf-8', toCsv(rows))
}

/** A yyyy-mm-dd stamp for filenames (local date). */
export function dateStamp(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10)
}

/** Slugify an event/label name for use in a filename. */
export function fileSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'export'
}
